import { useMemo, useState, type ReactNode } from "react"
import { HugeiconsIcon } from "@hugeicons/react"
import { Add01Icon, UnavailableIcon } from "@hugeicons/core-free-icons"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { CellPicker } from "@/components/cell-controls/cell-controls"
import { usePeople, type Person } from "@/api/people/usePeople"
import { usePersonMutations } from "@/api/people/usePersonMutations"
import { cn } from "@/lib/utils"

type PersonComboboxProps = {
  value: string | null
  onChange: (personId: string | null) => void
  placeholder?: string
  allowClear?: boolean
  allowCreate?: boolean
  className?: string
  size?: "sm" | "default"
  /** Триггер: cell (по умолчанию) или chip (для create-форм). */
  triggerVariant?: "cell" | "chip"
  /** Иконка слева (только в chip-варианте). */
  leadingIcon?: ReactNode
}

function initials(name: string) {
  return name
    .trim()
    .split(/\s+/)
    .map((part) => part[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase() || "??"
}

function personMatches(person: Person, query: string) {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return (
    person.name.toLowerCase().includes(q) ||
    (person.email?.toLowerCase().includes(q) ?? false)
  )
}

function PersonAvatar({ person, size = "sm" }: { person: Person; size?: "sm" | "md" }) {
  // Неприглашённый пользователь: показываем UnavailableIcon вместо аватарки.
  if (!person.isInvited) {
    return (
      <span
        className={cn(
          "inline-flex shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground/60",
          size === "sm" ? "size-4" : "size-5",
        )}
        title="Не приглашён"
      >
        <HugeiconsIcon
          icon={UnavailableIcon}
          strokeWidth={2}
          className={size === "sm" ? "size-2.5" : "size-3"}
        />
      </span>
    )
  }
  return (
    <Avatar className={cn("shrink-0", size === "sm" ? "size-4" : "size-5")}>
      <AvatarImage src={person.avatarUrl} />
      <AvatarFallback className={size === "sm" ? "text-[8px]" : "text-[9px]"}>
        {initials(person.name)}
      </AvatarFallback>
    </Avatar>
  )
}

export function PersonCombobox({
  value,
  onChange,
  placeholder = "Поиск...",
  allowClear = false,
  allowCreate = true,
  className,
  size: _size = "default",
  triggerVariant = "cell",
  leadingIcon,
}: PersonComboboxProps) {
  const { people, peopleById } = usePeople()
  const { createPerson } = usePersonMutations()
  const [query, setQuery] = useState("")

  const selected = value ? peopleById.get(value) ?? null : null

  const groups = useMemo(() => {
    const invited = people.filter((person) => person.isInvited)
    const uninvited = people.filter((person) => !person.isInvited)
    const result: { value: string; items: Person[] }[] = []
    if (invited.length > 0) result.push({ value: "Пользователи", items: invited })
    if (uninvited.length > 0) result.push({ value: "Не приглашённые", items: uninvited })
    return result
  }, [people])

  const exactMatch = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    if (!normalized) return true
    return people.some((person) => person.name.trim().toLowerCase() === normalized)
  }, [people, query])

  const canCreate =
    allowCreate &&
    query.trim().length > 0 &&
    !exactMatch &&
    !createPerson.isPending

  async function handleCreate() {
    const name = query.trim()
    if (!name) return
    const created = await createPerson.mutateAsync({ name })
    onChange(created.id)
    setQuery("")
  }

  return (
    <CellPicker
      className={className}
      triggerVariant={triggerVariant}
      trigger={
        selected ? (
          <span className="flex min-w-0 items-center gap-1.5">
            {triggerVariant === "chip" && leadingIcon ? (
              <span className="shrink-0 text-muted-foreground [&_svg]:size-3.5">
                {leadingIcon}
              </span>
            ) : null}
            {triggerVariant !== "chip" ? <PersonAvatar person={selected} size="sm" /> : null}
            <span className="truncate">{selected.name}</span>
          </span>
        ) : (
          <span className="flex min-w-0 items-center gap-1.5">
            {triggerVariant === "chip" && leadingIcon ? (
              <span className="shrink-0 text-muted-foreground [&_svg]:size-3.5">
                {leadingIcon}
              </span>
            ) : null}
            <span className="truncate text-muted-foreground/80">{placeholder || "Выберите…"}</span>
          </span>
        )
      }
    >
      {({ close }) => (
        <div>
          <div className="border-b p-1">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              className="h-7 px-2 text-[12px] font-normal border-input/60 focus-visible:border-input focus-visible:ring-0 focus-visible:ring-offset-0"
              autoFocus
            />
          </div>
          <ScrollArea className="max-h-64">
            <div className="p-1 space-y-0.5">
              {groups.length === 0 && (
                <div className="px-2 py-1.5 text-[12.5px] text-muted-foreground">
                  Никого не найдено
                </div>
              )}
              {groups.map((group) => {
                const filtered = group.items.filter((p) => personMatches(p, query))
                if (filtered.length === 0) return null
                return (
                  <div key={group.value} className="space-y-0.5">
                    <div className="px-1.5 py-1 text-[11px] font-normal text-muted-foreground/70">
                      {group.value}
                    </div>
                    {filtered.map((person) => {
                      const isSelected = selected?.id === person.id
                      return (
                        <button
                          key={person.id}
                          type="button"
                          onClick={() => {
                            onChange(person.id)
                            setQuery("")
                            close()
                          }}
                          className={cn(
                            "flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-[12.5px] leading-none font-normal transition-colors hover:bg-accent focus:bg-accent focus:outline-none",
                            isSelected && "bg-accent/50",
                          )}
                        >
                          <PersonAvatar person={person} size="md" />
                          <span className="truncate flex-1">{person.name}</span>
                          {!person.isInvited && (
                            <HugeiconsIcon
                              icon={UnavailableIcon}
                              strokeWidth={2}
                              className="size-3.5 shrink-0 text-muted-foreground/60"
                              aria-label="Не приглашён"
                            />
                          )}
                        </button>
                      )
                    })}
                  </div>
                )
              })}
            </div>
          </ScrollArea>

          {(canCreate || (allowClear && selected)) && <div className="border-t" />}

          {canCreate && (
            <div className="p-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 w-full justify-start px-2 text-[12.5px] font-normal"
                onClick={() => void handleCreate()}
                disabled={createPerson.isPending}
              >
                <HugeiconsIcon icon={Add01Icon} strokeWidth={2} className="size-3.5" />
                Создать "{query.trim()}"
              </Button>
            </div>
          )}

          {allowClear && selected && (
            <div className="p-1 pt-0">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 w-full justify-start px-2 text-[12.5px] font-normal text-muted-foreground"
                onClick={() => {
                  onChange(null)
                  close()
                }}
              >
                Очистить
              </Button>
            </div>
          )}
        </div>
      )}
    </CellPicker>
  )
}